import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle } from "lucide-react";

export default function Login() {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("demo@mukti.local");
  const [password, setPassword] = useState("Mukti-Demo-2026!");

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      await login(email, password);
      toast.success("Welcome back, investigator.");
      const dest = location.state?.from || "/";
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr_520px] bg-slate-50">
      <div
        className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(15,23,42,0.92), rgba(15,23,42,0.85)), url(https://images.pexels.com/photos/17483874/pexels-photo-17483874.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 grid place-items-center bg-white text-slate-900 rounded-md font-display font-bold text-lg">
            M
          </div>
          <div>
            <div className="font-display font-bold tracking-tight text-2xl leading-none">
              MUKTI
            </div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-300 mt-1">
              Intelligence Platform
            </div>
          </div>
        </div>

        <div className="max-w-md space-y-6">
          <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl leading-[1.05]">
            Investigative leads. <br />
            For human review.
          </h1>
          <p className="text-slate-200 text-base leading-relaxed">
            MUKTI surfaces patterns of overlapping phones, images and language
            across public advertisements so trained investigators can move
            faster on potential trafficking cases in Bangalore.
          </p>
          <div className="border border-amber-300/30 bg-amber-300/10 text-amber-100 text-xs px-4 py-3 rounded-md flex gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              This tool does not identify criminals or make legal conclusions.
              All decisions remain with the investigator.
            </span>
          </div>
        </div>

        <div className="text-xs text-slate-400 font-mono uppercase tracking-wider">
          MVP v1.0 · Authorized use only
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm space-y-6"
          data-testid="login-form"
        >
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 text-blue-700 text-xs font-mono uppercase tracking-wider">
              <ShieldCheck className="h-4 w-4" /> Investigator sign in
            </div>
            <h2 className="font-display font-bold tracking-tight text-3xl">
              Sign in to your case workspace
            </h2>
            <p className="text-slate-500 text-sm">
              Use the seeded demo account to explore the platform.
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="demo@mukti.local"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="w-full bg-slate-900 hover:bg-slate-800 h-10"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>

          <div className="text-xs text-slate-500 border border-dashed border-slate-300 rounded-md p-3 bg-white">
            <div className="font-medium text-slate-700 mb-1">Demo credentials</div>
            <div className="font-mono">demo@mukti.local / Mukti-Demo-2026!</div>
          </div>
        </form>
      </div>
    </div>
  );
}
